import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Zap, LogOut, Pencil, Loader2 } from "lucide-react";
import { LevelBar } from "@/components/XpChip";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, titleCase } from "@/lib/api";
import { BeltBar, BeltLabel } from "@/components/BeltBadge";
import { InstallCard } from "@/components/InstallCard";
import { ReminderCard } from "@/components/ReminderCard";
import { QuizHistory } from "@/components/QuizHistory";
import { PlanCard } from "@/components/PlanCard";
import { Avatar, AvatarUpload } from "@/components/Avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { BeltPicker, ChoiceRow, FREQ, GOALS, Label, STYLES } from "@/components/ProfileInputs";

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 eyebrow mb-2">{Icon && <Icon size={14} className={accent ? "text-[#E50914]" : ""} />}{label}</div>
      <div className="font-display text-3xl font-semibold leading-none">{value}</div>
    </div>
  );
}

function AvatarPickButton({ user }) {
  const { setUser } = useAuth();
  return (
    <label className="h-10 px-4 rounded-xl border border-[#27272A] text-sm font-semibold flex items-center cursor-pointer hover:border-[#E50914] transition-colors" data-testid="edit-photo-button">
      {user.avatar_version ? "Change photo" : "Add photo"}
      <input type="file" accept="image/*" className="hidden" data-testid="edit-photo-input" onChange={async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        const t = toast.loading("Uploading photo…");
        try {
          const { data } = await api.post("/profile/avatar", form);
          setUser(data);
          toast.success("Photo updated", { id: t });
        } catch (err) {
          toast.error(errorMessage(err), { id: t });
        }
      }} />
    </label>
  );
}

function RemovePhoto() {
  const { setUser } = useAuth();
  const remove = async () => {
    try {
      const { data } = await api.delete("/profile/avatar");
      setUser(data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <button type="button" data-testid="edit-remove-photo-button" onClick={remove} className="h-10 px-4 rounded-xl text-sm text-[#A1A1AA] hover:text-white transition-colors">Remove</button>
  );
}

function DeleteAccount() {
  const { logout } = useAuth();
  const remove = async () => {
    try {
      await api.delete("/account");
      logout();
      toast.success("Your account has been deleted");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="delete-account-button" className="w-full h-10 text-xs text-[#52525B] hover:text-[#FF3B30] transition-colors">Delete account</button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#161618] border-[#27272A] max-w-[90vw] sm:max-w-sm rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your account?</AlertDialogTitle>
          <AlertDialogDescription>This permanently deletes your profile, every logged session, your quiz history and XP. It can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="delete-account-confirm" onClick={remove} className="bg-[#E50914] hover:bg-[#B20710] text-white">Delete forever</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditSheet({ open, onOpenChange, user }) {
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);
  const form = f || {
    display_name: user.display_name, age: user.age ?? "", belt: user.belt, stripes: user.stripes, gym: user.gym || "",
    years_training: user.years_training ?? "", sessions_per_week: user.sessions_per_week, style: user.style, goal: user.goal || "",
  };
  const set = (p) => setF({ ...form, ...p });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile", {
        ...form, display_name: form.display_name.trim(), age: form.age === "" ? null : +form.age,
        years_training: form.years_training === "" ? null : +form.years_training, goal: form.goal || null,
      });
      setUser(data);
      toast.success("Profile updated");
      onOpenChange(false);
      setF(null);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setF(null); }}>
      <SheetContent side="bottom" className="bg-[#0A0A0A] border-[#27272A] max-w-md mx-auto rounded-t-3xl h-[90vh] overflow-y-auto px-5 pb-10" data-testid="profile-edit-sheet">
        <SheetHeader className="text-left mb-6">
          <SheetTitle className="font-display text-2xl uppercase tracking-tight">Edit profile</SheetTitle>
          <SheetDescription className="text-[#71717A]">Update your details anytime.</SheetDescription>
        </SheetHeader>
        <div className="space-y-7">
          <div className="flex items-center gap-4">
            <Avatar id={user.id} version={user.avatar_version} name={user.display_name} className="w-16 h-16 rounded-2xl" />
            <div className="flex gap-2">
              <AvatarPickButton user={user} />
              {user.avatar_version && <RemovePhoto />}
            </div>
          </div>
          <div><Label>Display name</Label><input data-testid="edit-name-input" className="field" maxLength={24} value={form.display_name} onChange={(e) => set({ display_name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Age</Label><input data-testid="edit-age-input" className="field" type="number" inputMode="numeric" value={form.age} onChange={(e) => set({ age: e.target.value })} /></div>
            <div><Label>Years training</Label><input data-testid="edit-years-input" className="field" type="number" inputMode="decimal" step="0.5" value={form.years_training} onChange={(e) => set({ years_training: e.target.value })} /></div>
          </div>
          <div><Label>Gym / academy</Label><input data-testid="edit-gym-input" className="field" maxLength={60} value={form.gym} onChange={(e) => set({ gym: e.target.value })} /></div>
          <div><Label>Belt</Label><BeltPicker belt={form.belt} stripes={form.stripes} onChange={set} /></div>
          <div><Label hint="Per week">Sessions</Label><ChoiceRow testid="edit-freq" options={FREQ.map((n) => ({ v: n, l: n === 7 ? "7+" : String(n) }))} value={form.sessions_per_week} onChange={(v) => set({ sessions_per_week: v })} className="grid grid-cols-7 gap-1.5" /></div>
          <div><Label>I mostly train</Label><ChoiceRow testid="edit-style" options={STYLES} value={form.style} onChange={(v) => set({ style: v })} className="grid grid-cols-3 gap-2" /></div>
          <div><Label>Main goal</Label><ChoiceRow testid="edit-goal" options={GOALS} value={form.goal} onChange={(v) => set({ goal: v })} /></div>
          <button data-testid="edit-save-button" onClick={save} disabled={busy || form.display_name.trim().length < 2} className="btn-primary">
            {busy ? <Loader2 size={18} className="animate-spin" /> : "Save changes"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/profile/stats").then((r) => r.data) });

  const toggleHidden = async (visible) => {
    try {
      const { data } = await api.put("/profile", { hide_from_leaderboard: !visible });
      setUser(data);
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const details = [
    user.age && `${user.age} yrs old`,
    user.years_training != null && `${user.years_training} yrs training`,
    user.sessions_per_week && `${user.sessions_per_week}${user.sessions_per_week === 7 ? "+" : ""}×/week`,
    { gi: "Gi", nogi: "No-Gi", both: "Gi & No-Gi" }[user.style],
    user.goal,
  ].filter(Boolean);

  return (
    <div className="px-5 pt-8 space-y-6">
      <section className="flex items-start gap-4 rise">
        <AvatarUpload user={user} onUser={setUser} className="w-16 h-16 rounded-2xl" testid="profile-avatar" />
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl font-bold uppercase tracking-tight leading-none truncate" data-testid="profile-name">{user.display_name}</h1>
          <div className="flex items-center gap-2.5 mt-2.5"><BeltBar belt={user.belt} stripes={user.stripes} className="w-16 h-3" /><BeltLabel belt={user.belt} stripes={user.stripes} /></div>
          {user.gym && <div className="text-sm text-[#71717A] mt-1 truncate" data-testid="profile-gym">{user.gym}</div>}
        </div>
        <button data-testid="profile-edit-button" onClick={() => setEditing(true)} className="w-10 h-10 rounded-full border border-[#27272A] flex items-center justify-center hover:border-[#E50914] transition-colors">
          <Pencil size={16} />
        </button>
      </section>

      {details.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="profile-details">
          {details.map((d) => <span key={d} className="text-xs px-2.5 py-1 rounded-md bg-[#161618] border border-[#27272A] text-[#A1A1AA]">{d}</span>)}
        </div>
      )}

      <PlanCard />

      <section className="card p-5" data-testid="profile-level">
        <LevelBar user={user} />
      </section>

      <section className="grid grid-cols-2 gap-3">
        <Stat label="Sessions" value={stats?.total_sessions ?? "–"} testid="profile-total-sessions" />
        <Stat label="Training days" value={stats?.training_days ?? "–"} testid="profile-training-days" />
      </section>
      {stats?.top_tag && <p className="text-sm text-[#A1A1AA] -mt-2" data-testid="profile-top-tag">Most logged: <span className="text-white font-semibold">{titleCase(stats.top_tag)}</span></p>}

      <QuizHistory />

      <section>
        <div className="eyebrow mb-3">Leaderboard</div>
        <label className="card p-4 flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <div className="text-sm font-semibold">Show me on the leaderboard</div>
            <div className="text-xs text-[#71717A] mt-0.5">Shows your name, photo, belt, XP and level only.</div>
          </div>
          <Switch data-testid="profile-leaderboard-switch" checked={!user.hide_from_leaderboard} onCheckedChange={toggleHidden} />
        </label>
      </section>

      <section>
        <div className="eyebrow mb-3">App</div>
        <div className="space-y-3">
          <ReminderCard />
          <InstallCard />
        </div>
      </section>

      <section className="pt-2 pb-6 space-y-2">
        <div className="text-xs text-center text-[#52525B]" data-testid="profile-email">{user.email}</div>
        <button data-testid="logout-button" onClick={logout} className="btn-secondary">
          <LogOut size={16} /> Log out
        </button>
        <div className="flex justify-center gap-5 pt-3 text-xs text-[#71717A]">
          <Link to="/terms" data-testid="profile-terms-link" className="hover:text-white transition-colors">Terms of Use</Link>
          <Link to="/privacy" data-testid="profile-privacy-link" className="hover:text-white transition-colors">Privacy Policy</Link>
        </div>
        <DeleteAccount />
      </section>

      <EditSheet open={editing} onOpenChange={setEditing} user={user} />
    </div>
  );
}
